import React, { useState } from "react";
import {
  Text,
  Box,
  List,
  ListItem,
  Button,
  IconButton,
  useDisclosure,
  VStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Divider,
  HStack,
  Icon,
  Center,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { useBatchContext } from "../../context/batch/BatchContext";
import {
  FaPlayCircle,
  FaRegTrashAlt,
  FaRunning,
} from "react-icons/fa";
import {
  createOrUpdateJob,
  deleteJob,
  runJob,
} from "../../services/BatchServices";
import { getModelById } from "../../services/DataModelService";
import { AddIcon } from "@chakra-ui/icons";
import { IoDuplicate, IoEllipsisVertical } from "react-icons/io5";
import { MdEdit } from "react-icons/md";
import showToast from "../../hooks/useCustomToast";
import { getAllReports } from "../../services/ReportPageServices";
import { sortBy } from "../../utils/sortUtils";
import { FilterProto, Header, ModelInfo } from "build-ts/metadata";
import { ColumnType, FilterOperator } from "build-ts/enums";
import { AnalysisActionRequest, JobRequest } from "build-ts/jobs_interface";
import CreateJobModal from "./modals/CreateJobModal";
import DeleteJobModal from "./modals/DeleteJobModal";
import RenameJobModal from "./modals/RenameJobModal";
import RunJobModal from "./modals/RunJobModal";
import { isDateFilterValid } from "utils/dateUtils";

const JobManagerContainer: React.FC = () => {
  const {
    jobHeaders,
    setJobHeaders,
    selectJob,
    createJob,
    selectedJob,
    metricHeaders,
    analysisActionRequest,
    setAnalysisActionRequest,
    selectedReport,
    reports,
    setSelectedReport,
    setSelectedJob,
    setSelectedPurpose,
    setSelectedContextMap,
    setChooseTargets,
    setSelectedEdgeToExclude,
    selectedJobId,
    setSelectedJobStepIndex,
    setReports,
    jobLoaders,
    setJobRunHeaders,
    jobRunHeaders,
    dateFilter,
    setDateFilter,
    setReportError,
    setDateError,
    dateError,
    reportError,
    setDateFilterCategory,
    dateFilterCategory,
  } = useBatchContext();

  const [newJobName, setNewJobName] = useState("");
  const [modelByMetricMap, setModelByMetricMap] = useState<{
    [ownerId: string]: ModelInfo;
  }>({});
  const [hoveredJobId, setHoveredJobId] = useState<string | null>(null);

  // Modal controls for creating a job
  const createJobModal = useDisclosure();
  // Modal controls for running a job
  const runJobModal = useDisclosure();
  // Modal controls for rename a job
  const renameJobModal = useDisclosure();
  // Modal controls for delete a job
  const deleteJobModal = useDisclosure();

  // Function to fetch all reports header and set them in the state
  const fetchReportsHeader = async() => {
    const response = await getAllReports("REPORT_BOOK");
    if(response){
      setReports([...response]);
    } else setReports([]);
  };

  // Function to fetch models and set them in the state
  const fetchAndSetModels = async () => {
    // Extract unique modelIds from jobSteps to avoid redundant API calls
    const modelIds = getModelIds();
    try {
      // Make API calls for all modelIds in parallel using Promise.all
      const modelPromises = modelIds.map((id) => getModelById(id));
      const modelResults = await Promise.all(modelPromises);

      // Create a map to store headers grouped by ownerId
      const ownerIdToHeadersMap: { [ownerId: string]: ModelInfo } = {};
      modelResults?.forEach((model) => {
        if (!model) return; // Skip null/undefined values
        analysisActionRequest?.forEach((step) => {
          const matchingHeader = metricHeaders.find(
            (header) => header.id === step.generate_tree_request?.metricId
          );
          if (matchingHeader?.owner_id === model.header?.id) {
            ownerIdToHeadersMap[matchingHeader?.id!] = model;
          }
        });
      });

      // Update the state with the constructed map
      setModelByMetricMap((prevMap) => ({
        ...prevMap, // Preserve existing key-value pairs
        ...ownerIdToHeadersMap, // Update with new key-value pairs
      }));
    } catch (error) {
      console.error("Error fetching model details:", error);
    }
  };

  // Function to get model ids from jobSteps
  const getModelIds = () => {
    const uniqueOwnerIds = new Set<string>();
    analysisActionRequest.forEach((step) => {
      const matchingHeader = metricHeaders.find(
        (header) => header.id === step?.generate_tree_request?.metricId
      );
      if (
        matchingHeader &&
        matchingHeader.owner_id &&
        !modelByMetricMap[matchingHeader.owner_id]
      ) {
        uniqueOwnerIds.add(matchingHeader.owner_id);
      }
    });
    return Array.from(uniqueOwnerIds);
  };

  // Function to handle job creation
  const handleCreateJob = async () => {
    if (newJobName.trim()) {
      await createJob(newJobName); // Call createJob with the name only
      setNewJobName(""); // Clear the input field
      createJobModal.onClose(); // Close the modal after creating a job
    }
  };

  // Function to handle job deletion
  const handleDeleteJob = async (jobId: string) => {
    await deleteJob(jobId); // Call the delete service with jobId
    setJobHeaders((prevHeaders) =>
      prevHeaders.filter((header) => header.id !== jobId)
    );
    setAnalysisActionRequest([]);
    setSelectedJob(null);
    setSelectedPurpose("");
    setSelectedContextMap({});
    setChooseTargets([]);
    setSelectedEdgeToExclude([]);
  };

  // Update contexts in job steps based on date and model information
  const validateInputs = () => {
    let valid = true;
    setReportError(null);
    setDateError(null);
    
    if (!selectedReport) {
      setReportError("Please select a report.");
      valid = false;
    }
  
    if (!isDateFilterValid(dateFilter!)) {
      setDateError("Please select a valid date filter.");
      valid = false;
    }
  
    if (!valid) return;

    handleRunJob(analysisActionRequest);
  };

  // Function to update the job with the updated job steps
  const handleRunJob = async (updatedJobSteps: AnalysisActionRequest[]) => {
    const globalContext: FilterProto[] = [];
    const uniqueTemporalName = new Set<string>();
    updatedJobSteps?.forEach((steps) => {
      const temporalField = modelByMetricMap[steps.generate_tree_request?.metricId!]
        .columns?.find((column) => column.column_type === ColumnType.TEMPORAL)?.header;
  
      if (temporalField && !uniqueTemporalName.has(temporalField.name!)) {
        const temporalContexts: FilterProto[] = [
          FilterProto.create({
            logical_column_header: { name: temporalField.name! },
            operator: FilterOperator.EQ,
            date_filter: dateFilter,
            values: []
          }),
        ];
        uniqueTemporalName.add(temporalField.name!);
  
        // Add each context to globalContext individually
        globalContext.push(...temporalContexts);
      }
    });
    
    const jobRequest = JobRequest.create({
      global_properties: {
        context_filters: globalContext,
        report_book_id: selectedReport?.id,
        report_book_name: "",
      },
      analysis_action_requests: updatedJobSteps,
    });
    const response = await createOrUpdateJob({
      id: selectedJob?.header?.id,
      name: selectedJob?.header?.name,
      request: jobRequest,
    });
    if (response?.request && runJobModal.isOpen) {
      // After update the job with global properties then set the latest job to state.
      setSelectedJob({header: response?.header, request: response.request, job_run_headers: response.job_run_headers });
      runJobModal.onClose();
      showToast("Job run has started", "", "warning");
      const runJobResponse = await runJob(response?.header?.id!, selectedJob?.header?.name!);
      if(runJobResponse?.header){
        const combinedHeaders = [runJobResponse.header , ...jobRunHeaders];
        const sortedJobRunHeader = sortBy(combinedHeaders, (header) => header.created_date_ms!, "desc");
        setJobRunHeaders([...sortedJobRunHeader]);
      }
    }
  };

  // Function to handle rename of a Job
  const handleRenameJob = async () => {
    const jobRequest = JobRequest.create({
      global_properties: selectedJob?.request?.global_properties,
      analysis_action_requests: analysisActionRequest,
    });
    const response = await createOrUpdateJob({
      id: selectedJob?.header?.id,
      name: newJobName,
      request: jobRequest,
    });
    if (response) {
      showToast("Job renamed", "", "success");
      setSelectedJob(response);
      const updatedJobs: Header[] = jobHeaders?.map((job) => {
        if (job.id === selectedJob?.header?.id) {
          return {
            ...job,
            name: newJobName,
          };
        }
        return job;
      });
      setJobHeaders(updatedJobs);
      renameJobModal.onClose();
    } else {
      showToast("Error while renaming", "", "error");
    }
  };

  const handleReportHeaderSelect = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const headerId = event.target.value;
    const selectedHeader = reports.find(
      (report) => report.id === headerId
    );
    if (selectedHeader) {
      setSelectedReport(selectedHeader);
    }
  };

  const duplicateJob = async() => {
    // Create the JobRequest JSON structure
    const jobRequest = JobRequest.create({
      global_properties: undefined,
      analysis_action_requests: analysisActionRequest
    });
    const response = await createOrUpdateJob({name: `${selectedJob?.header?.name} (copy)`, request: jobRequest});
    if(response){
      setJobHeaders((prevHeaders) => [...prevHeaders, response?.header!]);
      setSelectedJob({header: response?.header, request: response.request, job_run_headers: response.job_run_headers });
      setAnalysisActionRequest([...response.request?.analysis_action_requests ?? []]);
      setJobRunHeaders(response.job_run_headers);
      setSelectedJobStepIndex(null);
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" mb={0}>
      {/* Fixed Heading */}
      <Box p={1} height="auto">
        <HStack justify="space-between">
          <HStack>
            <Icon as={FaRunning} boxSize="4" color="black" />
            <Text
              fontWeight="bold"
              align="left"
              m={1}
              ml={0}
              color="gray.700"
              fontSize="sm"
            >
              Jobs
            </Text>
          </HStack>

          <VStack
            align="center"
            justify="center"
            cursor="pointer"
            borderRadius="md"
          >
            <Button
              aria-label="Add new item"
              leftIcon={<AddIcon boxSize={3} />}
              colorScheme="purple.700"
              size="xs"
              variant="outline"
              onClick={() => {
                createJobModal.onOpen();
                setNewJobName("");
              }}
            >
              New
            </Button>
          </VStack>
        </HStack>
        <Divider my={2} mb={0} borderColor="purple.700" />     
      </Box> 
      {/* Scrollable Content */}
      <Stack 
        height="100%"
        spacing="1" 
        alignItems="left"
        overflowY="auto"
        sx={{
          /* Custom scrollbar for WebKit-based browsers (Chrome, Safari, Edge) */
          "::-webkit-scrollbar": {
            width: "4px", // Set scrollbar width
          },
          "::-webkit-scrollbar-track": {
            background: "gray.100", // Background of the track
          },
          "::-webkit-scrollbar-thumb": {
            background: "gray.500", // Color of the scrollbar thumb
            borderRadius: "8px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "gray.600", // Color of the scrollbar thumb on hover
          },
          /* Scrollbar width for Firefox */
          scrollbarWidth: "thin", // For Firefox
          scrollbarColor: "gray.500 gray.100", // Thumb and track colors for Firefox
        }}
        >
        {jobLoaders.headerLoader ? (
          <Center height="20%">
            {/* Ensure the spinner is centered */}
            <Spinner size="md" color="purple.700" />
          </Center>
        ) : (
          <List>
            {jobHeaders?.map((header) => (
              <Box key={header.id}>
                <ListItem
                  role="group"
                  cursor="pointer"
                  onClick={() => selectJob(header)}
                  bg={
                    selectedJob?.header?.id === header.id
                      ? "purple.700"
                      : "transparent"
                  }
                  color={selectedJob?.header?.id === header.id ? "white" : "black"}
                  borderRadius="md"
                  p={1}
                  pl={2}
                  justifyContent="space-between"
                  display="flex"
                  alignItems="center"
                  fontSize="xs"
                  fontWeight={selectedJob?.header?.id === header.id ? 700 : 600}
                  _hover={{
                    bg:
                      selectedJob?.header?.id === header.id
                        ? "purple.700"
                        : "purple.50",
                  }}
                  textAlign="left"
                  onMouseEnter={() => setHoveredJobId(header?.id!)} // Track hovered job id
                  onMouseLeave={() => setHoveredJobId(null)} // Reset hovered job id
                >
                  {header.name}
                  <Box display="flex" alignItems="center">
                    <HStack spacing="0">
                      {/* Ellipsis Menu */}
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label="Options"
                          icon={<IoEllipsisVertical />}
                          variant="ghost"
                          size="xs"
                          color={
                            selectedJob?.header?.id === header.id
                              ? "white"
                              : "black"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if(selectedJobId !== hoveredJobId){
                              selectJob(header);
                            }
                          }}
                          opacity={selectedJob?.header?.id === header.id? "1": "0"}
                          _groupHover={{ opacity: "1" }}
                        />
                        <MenuList>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="black"
                            icon={<Icon as={FaPlayCircle} boxSize={4}/>}
                            onClick={(e) => {
                              e.stopPropagation();
                              runJobModal.onOpen();
                              fetchAndSetModels();
                              fetchReportsHeader();
                            }}
                          >
                            Run Job
                          </MenuItem>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="black"
                            icon={<Icon as={MdEdit} boxSize={4}/>}
                            onClick={(e) => {
                              e.stopPropagation();
                              setNewJobName(header?.name || "");
                              renameJobModal.onOpen();
                            }}
                          >
                            Rename
                          </MenuItem>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="black"
                            icon={<Icon as={IoDuplicate} boxSize={4}/>}
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateJob();
                            }}
                          >
                            Duplicate
                          </MenuItem>
                          <MenuItem
                            fontSize="xs"
                            fontWeight={600}
                            color="red"
                            icon={<Icon as={FaRegTrashAlt} boxSize={4}/>}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteJobModal.onOpen();
                            }}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Box>
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Stack>

      {/* Job Creation Modal */}
      <CreateJobModal
        isOpen={createJobModal.isOpen}
        onClose={createJobModal.onClose}
        newJobName={newJobName}
        setNewJobName={setNewJobName}
        handleCreateJob={handleCreateJob}
      />

      {/* Run Job Modal */}
      <RunJobModal 
        isOpen={runJobModal.isOpen}
        onClose={runJobModal.onClose}
        reports={reports}
        selectedReport={selectedReport}
        handleReportHeaderSelect={handleReportHeaderSelect}
        reportError={reportError}
        dateError={dateError}
        updateContextInJobSteps={validateInputs}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        setDateFilterCategory={setDateFilterCategory}
        dateFilterCategory={dateFilterCategory}
      />

      {/* Rename Job Modal */}
      <RenameJobModal 
        isOpen={renameJobModal.isOpen}
        onClose={renameJobModal.onClose}
        newJobName={newJobName}
        setNewJobName={setNewJobName}
        handleRenameJob={handleRenameJob}
      />

      {/* Delete Job Modal */}
      <DeleteJobModal 
        isOpen={deleteJobModal.isOpen}
        onClose={deleteJobModal.onClose}
        selectedJob={selectedJob}
        handleDeleteJob={handleDeleteJob}
      />
    </Box>
  );
};

export default JobManagerContainer;